/**
 * MoonTerminatorGlyph — pure SVG on-brand moon-phase terminator (DASH-FC1).
 *
 * Props:
 *   - `illumination` (0–100) — percentage lit, controls the ellipse x-radius
 *     (curvature of the terminator). 100 = full (no terminator, all lit);
 *     0 = new (no terminator, all dark).
 *   - `phaseValue` (0–1 SunCalc raw value) — determines waxing vs waning:
 *     < 0.5 → waxing (right limb lit), > 0.5 → waning (left limb lit),
 *     exactly 0.5 = full (treat as waxing for rendering, all lit).
 *
 * Geometry: a 20×20 SVG circle split by a path consisting of the outer disc
 * arc on the lit side and the terminator ellipse on the dark side. The
 * terminator ellipse x-radius is derived from illumination; y-radius equals
 * the disc radius (always a vertical ellipse). The string `phase` is NEVER
 * consumed for geometry — only the two numeric props drive rendering.
 *
 * Self-contained: no `@/services` or `@/stores` imports. aria-hidden so screen
 * readers skip decorative SVG. data-testid provided for tests.
 */

interface MoonTerminatorGlyphProps {
  /** Illumination 0–100 (percentage lit). */
  illumination: number;
  /** SunCalc raw phase value 0–1 (< 0.5 = waxing; > 0.5 = waning). */
  phaseValue: number;
}

// Static geometry.
const SVG_SIZE = 20;
const R = 9; // disc radius (leaves 1px margin in 20×20 viewBox)
const CX = SVG_SIZE / 2; // 10
const CY = SVG_SIZE / 2; // 10

// Disc fill colors.
const LIT_COLOR = 'var(--color-text-secondary, #c8c5c0)';
const DARK_COLOR = 'var(--color-bark-700, #3d3a35)';
const DISC_STROKE = 'var(--color-border-subtle, #2d2a26)';

/**
 * Compute the terminator ellipse x-radius from illumination (0–100).
 *
 * When illumination = 100 (full moon), there is no terminator: the entire
 * disc is lit. When illumination = 0 (new moon), the entire disc is dark.
 * For intermediate values the x-radius of the terminator ellipse spans from
 * R (at 0 and 100, degenerate) through 0 at exactly 50% illumination (a
 * straight line terminator = quarter moon). We map:
 *
 *   fraction = illumination / 100  (0..1)
 *   The "wobble" is |2*fraction - 1| (0 at new/full, 1 at quarter).
 *   ell_x = R * |2*fraction - 1|   (0 at quarter = straight line; R at new/full)
 *
 * Concave vs convex direction of the ellipse is controlled by the sweep flag.
 */
function terminatorXRadius(illumination: number): number {
  const fraction = Math.max(0, Math.min(100, illumination)) / 100;
  // |2f-1| maps 0→1, 0.5→0, 1→1
  return R * Math.abs(2 * fraction - 1);
}

export function MoonTerminatorGlyph({ illumination, phaseValue }: MoonTerminatorGlyphProps) {
  // Clamp inputs.
  const illum = Math.max(0, Math.min(100, illumination));
  const phase = Math.max(0, Math.min(1, phaseValue));

  // Full moon: all lit — just a filled circle.
  if (illum >= 100) {
    return (
      <svg
        data-testid="moon-terminator-glyph"
        width={SVG_SIZE}
        height={SVG_SIZE}
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        aria-hidden="true"
      >
        <circle cx={CX} cy={CY} r={R} fill={LIT_COLOR} stroke={DISC_STROKE} strokeWidth="0.5" />
      </svg>
    );
  }

  // New moon: all dark — just a filled dark circle.
  if (illum <= 0) {
    return (
      <svg
        data-testid="moon-terminator-glyph"
        width={SVG_SIZE}
        height={SVG_SIZE}
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        aria-hidden="true"
      >
        <circle cx={CX} cy={CY} r={R} fill={DARK_COLOR} stroke={DISC_STROKE} strokeWidth="0.5" />
      </svg>
    );
  }

  // Intermediate: composite path.
  // Waxing (phaseValue < 0.5): right limb is lit (lit side on right).
  // Waning (phaseValue >= 0.5): left limb is lit (lit side on left).
  const isWaxing = phase < 0.5;

  // The terminator ellipse x-radius.
  const ex = terminatorXRadius(illum);
  const ey = R; // always full height

  // The moon path is built from:
  //   - An arc of the outer disc on the lit side (large arc for > 50% illumination,
  //     small arc for < 50%)
  //   - An ellipse arc as the terminator (sweep direction determines concave/convex)
  //
  // Top and bottom of the disc: (CX, CY - R) and (CX, CY + R).
  // The disc arc and ellipse arc connect these two points.
  //
  // For the outer disc (from top to bottom):
  //   large-arc-flag = 1 when the lit side is > 50% (> half the disc)
  // illumination > 50 → large arc on lit side; < 50 → small arc.
  const largeArcFlag = illum >= 50 ? 1 : 0;

  // Outer disc sweep: lit half goes from top to bottom on the correct side.
  // Waxing (lit right): the right half arc sweeps clockwise from top to bottom.
  // Waning (lit left): the left half arc sweeps counter-clockwise from top to bottom.
  const discSweep = isWaxing ? 1 : 0;

  // Terminator ellipse sweep: curves back on the dark side.
  // Waxing: the terminator ellipse sweeps counter-clockwise (creating the crescent shape).
  // For < 50% lit (waxing crescent), the terminator bulges LEFT (toward dark side).
  // For > 50% lit (waxing gibbous), the terminator curves RIGHT slightly.
  // The sweep flag inverts for the two halves of the moon's cycle:
  //   - Waxing crescent (0–50%): terminator sweeps CCW (flag=0)
  //   - Waxing gibbous  (50–100%): terminator sweeps CW (flag=1)
  //   - Waning gibbous  (50–100% from full): terminator sweeps CCW (flag=0)
  //   - Waning crescent (0–50%): terminator sweeps CW (flag=1)
  //
  // Simplified rule: terminatorSweep = isWaxing ? (illum < 50 ? 0 : 1) : (illum < 50 ? 1 : 0)
  const terminatorSweep = isWaxing ? (illum < 50 ? 0 : 1) : illum < 50 ? 1 : 0;

  // Disc top/bottom anchors.
  const topY = CY - R;
  const botY = CY + R;

  // Path: start at top, disc arc to bottom (lit half), terminator ellipse back to top.
  const path = [
    `M ${CX} ${topY}`,
    `A ${R} ${R} 0 ${largeArcFlag} ${discSweep} ${CX} ${botY}`,
    `A ${ex} ${ey} 0 ${largeArcFlag} ${terminatorSweep} ${CX} ${topY}`,
    'Z',
  ].join(' ');

  return (
    <svg
      data-testid="moon-terminator-glyph"
      width={SVG_SIZE}
      height={SVG_SIZE}
      viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      aria-hidden="true"
    >
      {/* Dark disc background */}
      <circle cx={CX} cy={CY} r={R} fill={DARK_COLOR} stroke={DISC_STROKE} strokeWidth="0.5" />
      {/* Lit region */}
      <path d={path} fill={LIT_COLOR} />
    </svg>
  );
}
